import { useState } from "react";
import { useLocation } from "react-router";
import { FileText, Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FileDropzone } from "@/components/FileDropzone";
import { useSendFeedback } from "@/lib/tanstack-query/feedback";
import { isHttpError } from "@/lib/api-client";

const MAX_LENGTH = 5000;
const MAX_FILES = 3;
const MAX_SIZE_MB = 10;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

const ALLOWED_MIME_PATTERNS = [
  "image/*",
  "video/*",
  "application/pdf",
  "text/plain",
  "text/csv",
  "text/markdown",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];
const EXTENSION_FALLBACKS = [".md", ".markdown", ".csv", ".txt"];
const ACCEPT = [...ALLOWED_MIME_PATTERNS, ...EXTENSION_FALLBACKS].join(",");

const isAllowedFile = (file: File): boolean => {
  const type = file.type.toLowerCase();
  if (type) {
    const mimeOk = ALLOWED_MIME_PATTERNS.some((pattern) => {
      if (pattern === type) return true;
      const [pT, pS] = pattern.split("/");
      const [fT, fS] = type.split("/");
      return pT === fT && (pS === "*" || pS === fS);
    });
    if (mimeOk) return true;
  }
  const ext = file.name.toLowerCase().split(".").pop();
  return !!ext && EXTENSION_FALLBACKS.includes(`.${ext}`);
};

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface SendFeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SendFeedbackModal = ({
  open,
  onOpenChange,
}: SendFeedbackModalProps) => {
  const location = useLocation();
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const mutation = useSendFeedback();

  const reset = () => {
    setMessage("");
    setFiles([]);
    setAttachmentError(null);
  };

  const handleFilesSelected = (incoming: File[]) => {
    setAttachmentError(null);
    const badMime = incoming.filter((f) => !isAllowedFile(f));
    if (badMime.length > 0) {
      setAttachmentError(
        `Unsupported file type: ${badMime.map((f) => f.name).join(", ")}.`,
      );
      return;
    }
    const tooBig = incoming.filter((f) => f.size > MAX_SIZE_BYTES);
    if (tooBig.length > 0) {
      setAttachmentError(
        `Files over ${MAX_SIZE_MB} MB: ${tooBig.map((f) => f.name).join(", ")}.`,
      );
      return;
    }
    if (files.length + incoming.length > MAX_FILES) {
      setAttachmentError(`At most ${MAX_FILES} files.`);
      return;
    }
    setFiles((prev) => [...prev, ...incoming]);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setAttachmentError(null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = message.trim();
    if (!trimmed) return;

    try {
      await mutation.mutateAsync({
        message: trimmed,
        page: location.pathname,
        attachments: files.length > 0 ? files : undefined,
      });
      toast.success("Thanks for the feedback!");
      reset();
      onOpenChange(false);
    } catch (err) {
      if (isHttpError(err)) {
        if (err.status === 429) {
          toast.error(
            err.message || "You've sent feedback recently, try again shortly.",
          );
          return;
        }
        if (err.status === 400) {
          if (err.message?.startsWith("Unsupported file type")) {
            toast.error(err.message);
            return;
          }
          toast.error("Couldn't send feedback. Check your attachments.");
          return;
        }
      }
      toast.error("Failed to send feedback. Try again.");
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (mutation.isPending) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const remaining = MAX_LENGTH - message.length;
  const canAddMore = files.length < MAX_FILES;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Send feedback</DialogTitle>
          <DialogDescription>
            Tell us what's working, what's broken, or what you'd like to see.
            Your message goes straight to the team.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="feedback-message">
              Message <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="feedback-message"
              placeholder="What's on your mind?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={mutation.isPending}
              rows={6}
              maxLength={MAX_LENGTH}
              autoFocus
            />
            <div className="flex justify-end text-xs text-muted-foreground">
              <span className={remaining < 100 ? "text-yellow-600" : ""}>
                {message.length} / {MAX_LENGTH}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Attachments</Label>
            <FileDropzone
              multiple
              accept={ACCEPT}
              acceptDescription="image, video, PDF, Office, or text"
              disabled={mutation.isPending || !canAddMore}
              onFilesSelect={handleFilesSelected}
            >
              {({ openPicker, isDragging }) => (
                <button
                  type="button"
                  onClick={openPicker}
                  disabled={mutation.isPending || !canAddMore}
                  className={`w-full py-4 px-4 border-2 border-dashed rounded-md flex flex-col items-center justify-center gap-1 transition-colors text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    isDragging
                      ? "border-primary bg-primary/5"
                      : "border-input hover:bg-muted"
                  } ${
                    mutation.isPending || !canAddMore
                      ? "cursor-not-allowed opacity-60"
                      : ""
                  }`}
                >
                  <Paperclip className="h-5 w-5 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    {canAddMore
                      ? "Drop files or click to attach"
                      : `Maximum ${MAX_FILES} files reached`}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Up to {MAX_FILES} files, {MAX_SIZE_MB} MB each
                  </span>
                </button>
              )}
            </FileDropzone>

            {files.length > 0 && (
              <ul className="flex flex-col gap-1.5">
                {files.map((f, i) => (
                  <li
                    key={`${f.name}-${f.size}-${i}`}
                    className="flex items-center gap-2 p-2 border rounded-md bg-muted/50 text-sm"
                  >
                    <FileText className="h-4 w-4 text-primary shrink-0" />
                    <span
                      className="font-medium truncate flex-1 min-w-0"
                      title={f.name}
                    >
                      {f.name}
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {formatSize(f.size)}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      disabled={mutation.isPending}
                      aria-label={`Remove ${f.name}`}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {attachmentError && (
              <p className="text-sm text-destructive">{attachmentError}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!message.trim() || mutation.isPending}
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
